import { NODE_TYPES } from './nodeIcons'
import type { MockNode, MockNodeType } from './types'

/**
 * Filter state for the Knowledge Graph page (DSGN-63, Figma "Filter"
 * frames), kept out of the menu so that file exports only a component — the
 * same reason `TagMapping/filters.ts` sits beside its menu.
 *
 * Two facets, as the design draws them: Entity Type and Uploaded by. Each is
 * a multi-select and an empty list means "no filter". Entity type is applied
 * by the canvas through `enabledTypes` (the legend toggles the same set);
 * Uploaded by is applied here, as the set of node ids the canvas dims.
 */
export interface KgFilters {
  types: MockNodeType[]
  /** Uploader display names, as the hierarchy reports them. */
  uploaders: string[]
}

export const EMPTY_KG_FILTERS: KgFilters = { types: [], uploaders: [] }

export type KgFacet = keyof KgFilters

export const FACET_ORDER: readonly KgFacet[] = ['types', 'uploaders']

/** One chip per selected value, in the menu's order. */
export const activeFacets = (filters: KgFilters): Array<{ facet: KgFacet; value: string }> =>
  FACET_ORDER.flatMap((facet) => filters[facet].map((value) => ({ facet, value })))

export const countActive = (filters: KgFilters): number => activeFacets(filters).length

export const toggleValue = <F extends KgFacet>(filters: KgFilters, facet: F, value: KgFilters[F][number]): KgFilters => {
  const list = filters[facet] as string[]
  const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  return { ...filters, [facet]: next }
}

/**
 * The entity types the Filter offers: those the graph has, in the legend's
 * order, plus any already picked so they can be unpicked. Not every type the
 * app knows: the agent graph has no Site, Operation Unit or Documents, and
 * the legend stopped listing them (#37071).
 */
export const entityTypeOptions = (present: ReadonlySet<MockNodeType>, picked: readonly MockNodeType[]): MockNodeType[] =>
  NODE_TYPES.filter((type) => present.has(type) || picked.includes(type))

/**
 * A legend click on `type`. From "everything" (no type picked) it means
 * "everything the graph has but this one", so the chips name only types on
 * the canvas; it used to pick every type the app knows, and one click put
 * seven chips under the toolbar. Enterprise is a type like the rest: it used
 * to be left out of that list, which greyed it in the legend and the Filter
 * while the canvas drew it regardless.
 *
 * An empty list means "no filter", so turning off the last type on is
 * refused rather than turning every type back on.
 */
export const toggleLegendType = (filters: KgFilters, type: MockNodeType, present: ReadonlySet<MockNodeType>): KgFilters => {
  const types =
    filters.types.length === 0
      ? NODE_TYPES.filter((t) => present.has(t) && t !== type)
      : toggleValue(filters, 'types', type).types
  return types.length > 0 ? { ...filters, types } : filters
}

export const removeValue = (filters: KgFilters, facet: KgFacet, value: string): KgFilters => ({
  ...filters,
  [facet]: (filters[facet] as string[]).filter((v) => v !== value),
})

/**
 * Node ids the Uploaded by facet excludes. A sheet carries its document's
 * uploader, so a sheet whose uploader is not chosen (or unknown) is excluded,
 * and so is everything hanging under it — the equipment drawn on that sheet.
 * The root, which no one uploaded, is never excluded, so the graph keeps its
 * anchor. Empty set when the facet is off.
 */
export const excludedNodeIds = (nodes: MockNode[], filters: KgFilters): Set<string> => {
  const out = new Set<string>()
  if (filters.uploaders.length === 0) return out
  for (const node of nodes) {
    if (node.documentId === undefined && node.uploadedBy === undefined) continue
    if (!(node.uploadedBy !== undefined && filters.uploaders.includes(node.uploadedBy))) out.add(node.id)
  }
  // Children of an excluded node go with it; parents come before children in
  // the agent graph, but walk until stable so the order cannot matter.
  let grew = true
  while (grew) {
    grew = false
    for (const node of nodes) {
      if (node.parent && out.has(node.parent) && !out.has(node.id)) {
        out.add(node.id)
        grew = true
      }
    }
  }
  return out
}

/** The uploaders present in the graph, alphabetical, for the menu's list. */
export const presentUploaders = (nodes: MockNode[]): string[] => {
  const seen = new Set<string>()
  for (const node of nodes) if (node.uploadedBy) seen.add(node.uploadedBy)
  return [...seen].sort((a, b) => a.localeCompare(b))
}
